import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Table, Spinner, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCashRegister } from '@fortawesome/free-solid-svg-icons';
import MetricCardWithComparison from './MetricCardWithComparison';
import DashboardChart from './DashboardChart';
import { withNamespaces } from 'react-i18next';
import * as DashboardController from '../Controller/DashboardController';
import moment from 'moment';
import Utility from '../../../../GlobalModules/Utility/Utility';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';


function MainContent({ t, accessToken }) {
  const [dashboardMonth, setDashboardMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('TM'); // Default to This Month
  const [dateRange, setDateRange] = useState(() => {
    const today = moment();
    const startDate = today.clone().startOf('month');
    const endDate = today.clone().endOf('month');
    return {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD')
    };
  });

  // Dashboard statistics state
  const [todaysOrderCount, setTodaysOrderCount] = useState({ raw: 0, formatted: 0, difference: 0 });
  const [todaysOrderValue, setTodaysOrderValue] = useState({ raw: 0, formatted: 0, difference: 0 });
  // orderCount is used for internal data tracking even if not directly displayed
  const [orderCount, setOrderCount] = useState({ raw: 0, formatted: 0 });
  const [orderValue, setOrderValue] = useState({ raw: 0, formatted: 0 });
  const [revenueValue, setRevenueValue] = useState({ raw: 0, formatted: 0 });
  const [customerCount, setCustomerCount] = useState({ raw: 0, formatted: 0 });
  const [expense, setExpense] = useState({ raw: 0, formatted: 0 });
  const [netProfitValue, setNetProfitValue] = useState({ raw: 0, formatted: 0 });
  const [purchaseOrderCount, setPurchaseOrderCount] = useState({ raw: 0, formatted: 0 });
  const [invoicesCount, setInvoicesCount] = useState({ raw: 0, formatted: 0 });
  const [footfallValue, setFootfallValue] = useState(0);
  const [target, setTarget] = useState({
    income: 0,
    income_width: '0%',
    expense: 0,
    expense_width: '0%',
    sales: 0,
    sales_width: '0%',
    net_profit: 0,
    net_profit_width: '0%'
  });

  // Chart data state
  const [salesCountChartData, setSalesCountChartData] = useState({ labels: [], datasets: [] });
  const [salesValueChartData, setSalesValueChartData] = useState({ labels: [], datasets: [] });
  const [incomeExpenseChartData, setIncomeExpenseChartData] = useState({
    labels: [t('Income'), t('Expense')],
    datasets: [{
      data: [0, 0],
      backgroundColor: ['#28a745', '#dc3545']
    }]
  });

  // Monthly activity chart data
  const [monthlySalesCountChartData, setMonthlySalesCountChartData] = useState({ labels: [], datasets: [] });
  const [monthlySalesValueChartData, setMonthlySalesValueChartData] = useState({ labels: [], datasets: [] });
  const [monthlyChartLoading, setMonthlyChartLoading] = useState(true);

  // Transactions state
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  const storeCurrency = localStorage.getItem('store_currency') || 'INR';
  const [topSellingLowSellingProductsData, setTopSellingLowSellingProductsData] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [lowStockLoading, setLowStockLoading] = useState(true);
  const [orderTypeWiseStats, setOrderTypeWiseStats] = useState([]);
  const [orderTypeStatsLoading, setOrderTypeStatsLoading] = useState(true);
  
  // Tooltip definitions for filter buttons
  const filterTooltips = {
    'TD': 'Today',
    'YD': 'Yesterday', 
    'TW': 'This Week',
    'LW': 'Last Week',
    'TM': 'This Month',
    'LM': 'Last Month',
    'L6M': 'Last 6 Months',
    'TQ': 'This Quarter',
    'LQ': 'Last Quarter',
    'TY': 'This Year',
    'LY': 'Last Year',
    'CFY': 'Current Fiscal Year',
    'LFY': 'Last Fiscal Year'
  };
  
  // Calculate date range based on filter type
  const calculateDateRange = (filterType) => {
    let startDate, endDate;
    const today = moment();
    
    switch(filterType) {
      case 'TD': // Today
        startDate = today.clone().startOf('day');
        endDate = today.clone().endOf('day');
        break;
      case 'YD': // Yesterday
        startDate = today.clone().subtract(1, 'days').startOf('day');
        endDate = today.clone().subtract(1, 'days').endOf('day');
        break;
      case 'TW': // This Week
        startDate = today.clone().startOf('week');
        endDate = today.clone().endOf('week');
        break;
      case 'LW': // Last Week
        startDate = today.clone().subtract(1, 'weeks').startOf('week');
        endDate = today.clone().subtract(1, 'weeks').endOf('week');
        break;
      case 'TM': // This Month
        startDate = today.clone().startOf('month');
        endDate = today.clone().endOf('month');
        break;
      case 'LM': // Last Month
        startDate = today.clone().subtract(1, 'months').startOf('month');
        endDate = today.clone().subtract(1, 'months').endOf('month');
        break;
      case 'L6M': // Last 6 Months
        startDate = today.clone().subtract(6, 'months').startOf('day');
        endDate = today.clone().endOf('day');
        break;
      case 'TQ': // This Quarter
        startDate = today.clone().startOf('quarter');
        endDate = today.clone().endOf('quarter');
        break;
      case 'LQ': // Last Quarter
        startDate = today.clone().subtract(1, 'quarters').startOf('quarter');
        endDate = today.clone().subtract(1, 'quarters').endOf('quarter');
        break;
      case 'TY': // This Year
        startDate = today.clone().startOf('year');
        endDate = today.clone().endOf('year');
        break;
      case 'LY': // Last Year
        startDate = today.clone().subtract(1, 'years').startOf('year');
        endDate = today.clone().subtract(1, 'years').endOf('year');
        break;
      case 'CFY': // Current Fiscal Year (assuming April-March)
        const currentMonth = today.month();
        if (currentMonth >= 3) { // April (3) onwards
          startDate = today.clone().month(3).startOf('month'); // April
          endDate = today.clone().add(1, 'year').month(2).endOf('month'); // March next year
        } else {
          startDate = today.clone().subtract(1, 'year').month(3).startOf('month');
          endDate = today.clone().month(2).endOf('month');
        }
        break;
      case 'LFY': // Last Fiscal Year
        const currentMonthLFY = today.month();
        if (currentMonthLFY >= 3) {
          startDate = today.clone().subtract(1, 'year').month(3).startOf('month');
          endDate = today.clone().month(2).endOf('month');
        } else {
          startDate = today.clone().subtract(2, 'years').month(3).startOf('month');
          endDate = today.clone().subtract(1, 'year').month(2).endOf('month');
        }
        break;
      default:
        startDate = today.clone().startOf('day');
        endDate = today.clone().endOf('day');
    }
    
    return {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD')
    };
  };
  
  // Handle filter button click
  const handleFilterClick = (filterType) => {
    setSelectedFilter(filterType);
    const newDateRange = calculateDateRange(filterType);
    setDateRange(newDateRange);
    
    // Trigger data refresh
    setTimeout(() => {
      const event = new CustomEvent('dashboardStoreChange');
      window.dispatchEvent(event);
    }, 100);
  };
  
  // Handle month change in date picker
  const handleMonthChange = (date) => {
    if (date) {
      setSelectedDate(date);
      const formattedMonth = moment(date).format('YYYY-MM');
      setDashboardMonth(formattedMonth);
    } else {
      const currentDate = new Date();
      setSelectedDate(currentDate);
      const formattedMonth = moment(currentDate).format('YYYY-MM');
      setDashboardMonth(formattedMonth);
    }
  };

  // Data fetching functions
  const fetchMonthlyChartData = useCallback(async () => {
    setMonthlyChartLoading(true);
    try {
      // const response = await DashboardController.getOrderChartStats(dashboardMonth);
      const response = await DashboardController.getOrderChartStats(dateRange.start_date, dateRange.end_date);

      if (response && response.success === true) {
        const data = response.data;

        // Set monthly sales count chart data
        if (data.x_axis && data.y_axis && data.y_axis.order_count) {
          setMonthlySalesCountChartData({
            labels: data.x_axis || [],
            datasets: [{
              label: t('Order Count'),
              data: data.y_axis.order_count || [],
              fill: true,
              backgroundColor: 'rgba(40, 167, 69, 0.2)',
              borderColor: '#28a745',
              pointBackgroundColor: '#28a745',
              pointBorderColor: '#fff',
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: '#28a745',
              pointStyle: 'circle',
              pointRadius: 2,
              borderWidth: 1,
              tension: 0.4
            }]
          });
        }

        // Set monthly sales value chart data
        if (data.x_axis && data.y_axis && data.y_axis.order_value) {
          setMonthlySalesValueChartData({
            labels: data.x_axis || [],
            datasets: [{
              label: `${t('Order Value')} (${storeCurrency})`,
              data: data.y_axis.order_value || [],
              fill: true,
              backgroundColor: 'rgba(0, 123, 255, 0.2)',
              borderColor: '#007bff',
              pointBackgroundColor: '#007bff',
              pointBorderColor: '#fff',
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: '#007bff',
              pointStyle: 'circle',
              pointRadius: 2,
              borderWidth: 1,
              tension: 0.4
            }]
          });
        }
      }
    } catch (error) {
      console.error('Error fetching monthly chart data:', error);
    } finally {
      setMonthlyChartLoading(false);
    }
  }, [dashboardMonth, t, storeCurrency, dateRange]);

  const fetchRecentTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      const formattedDate = moment(dashboardMonth).format('YYYY-MM');

      // Only pass the date parameter since storeId is retrieved inside the controller function
      // const response = await DashboardController.getRecentTransactions(formattedDate);
      const response = await DashboardController.getRecentTransactions(dateRange.start_date, dateRange.end_date);

      if (response && response.success === true) { // Explicitly check for true
        // Make sure we're setting the array of transactions correctly
        const transactions = Array.isArray(response.data) ? response.data : [];
        setRecentTransactions(transactions);
      } else {
        // Reset transactions if the response was not successful
        setRecentTransactions([]);
      }
    } catch (error) {
      // Handle error silently
      setRecentTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  }, [dashboardMonth,dateRange]);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await DashboardController.getDashboardStats(dateRange.start_date, dateRange.end_date);
      // console.log('Dashboard response:', response);

      if (response.success === true) {
        const data = response.data;

        // Update state with response data
        setTodaysOrderCount({
          raw: data.todays_order_count?.count_raw || 0,
          formatted: data.todays_order_count?.count_formatted || 0,
          difference: data.todays_order_count?.difference || 0
        });

        setTodaysOrderValue({
          raw: data.todays_order_value?.count_raw || 0,
          formatted: data.todays_order_value?.count_formatted || 0,
          difference: data.todays_order_value?.difference || 0
        });

        setOrderCount({
          raw: data.order_count?.count_raw || 0,
          formatted: data.order_count?.count_formatted || 0
        });

        setOrderValue({
          raw: data.order_value?.count_raw || 0,
          formatted: data.order_value?.count_formatted || 0
        });

        setRevenueValue({
          raw: data.revenue_value?.count_raw || 0,
          formatted: data.revenue_value?.count_formatted || 0
        });

        setCustomerCount({
          raw: data.customer_count?.count_raw || 0,
          formatted: data.customer_count?.count_formatted || 0
        });

        setExpense({
          raw: data.expense?.count_raw || 0,
          formatted: data.expense?.count_formatted || 0
        });

        setNetProfitValue({
          raw: data.net_profit_value?.count_raw || 0,
          formatted: data.net_profit_value?.count_formatted || 0
        });

        setPurchaseOrderCount({
          raw: data.purchase_order_count?.count_raw || 0,
          formatted: data.purchase_order_count?.count_formatted || 0,
          difference: data.purchase_order_count?.difference || 0
        });

        setInvoicesCount({
          raw: data.invoices_count?.count_raw || 0,
          formatted: data.invoices_count?.count_formatted || 0,
          difference: data.invoices_count?.difference || 0
        });

        setFootfallValue(data.footfall || 0);

        if (data.targets) {
          setTarget({
            income: data.targets.income || 0,
            income_width: `${(data.revenue_value?.count_raw / data.targets.income) * 100 || 0}%`,
            expense: data.targets.expense || 0,
            expense_width: `${(data.expense?.count_raw / data.targets.expense) * 100 || 0}%`,
            sales: data.targets.sales || 0,
            sales_width: `${(data.order_value?.count_raw / data.targets.sales) * 100 || 0}%`,
            net_profit: data.targets.net_profit || 0,
            net_profit_width: `${(data.net_profit_value?.count_raw / data.targets.net_profit) * 100 || 0}%`,
          });
        }

        // Chart data
        // Set income/expense chart data
        setIncomeExpenseChartData({
          labels: [t('Income'), t('Expense')],
          datasets: [{
            data: [data.revenue_value?.count_raw || 0, data.expense?.count_raw || 0],
            backgroundColor: ['#28a745', '#dc3545'],
            borderWidth: 1
          }]
        });

        if (data.todays_order_count?.chart) {
          setSalesCountChartData({
            labels: data.todays_order_count.chart.x_axis || [],
            datasets: [{
              data: data.todays_order_count.chart.y_axis || [],
              backgroundColor: '#28a745',
              borderWidth: 1,
              tension: 0.4
            }]
          });
        }

        if (data.todays_order_value?.chart) {
          setSalesValueChartData({
            labels: data.todays_order_value.chart.x_axis || [],
            datasets: [{
              label: t('Sales Value'),
              data: data.todays_order_value.chart.y_axis || [],
              fill: true,
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              borderColor: 'rgba(75, 192, 192, 1)',
              pointBackgroundColor: 'rgba(75, 192, 192, 1)',
              pointBorderColor: '#fff',
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: 'rgba(75, 192, 192, 1)',
              pointStyle: 'circle',
              pointRadius: 2,
              borderWidth: 1,
              tension: 0.4
            }]
          });
        }
      } else {
        console.log('Error:', response.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      console.log('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, t]);

  const fetchTopSellingLowSellingProducts = useCallback(async () => {
    try {
      const storeId = localStorage.getItem('store_id');
      const formattedDate = moment(dashboardMonth).format('YYYY-MM');

      const requestData = {
        // date: formattedDate,
        store_id: storeId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      };

      const response = await DashboardController.topSellingLowSellingProducts(requestData);

      if (response && response.success === true) {
        setTopSellingLowSellingProductsData(response.data);
      }
    } catch (error) {
      console.error('Error fetching top selling low selling products:', error);
    }
  }, [dashboardMonth, dateRange]);

  const fetchLowStockProducts = useCallback(async () => {
    setLowStockLoading(true);
    try {
      const response = await DashboardController.getLowStockProducts();

      if (response && response.success === true) {
        setLowStockProducts(response.data || []);
      } else {
        setLowStockProducts([]);
      }
    } catch (error) {
      console.error('Error fetching low stock products:', error);
      setLowStockProducts([]);
    } finally {
      setLowStockLoading(false);
    }
  }, []);

  const fetchOrderTypeWiseStats = useCallback(async () => {
    setOrderTypeStatsLoading(true);
    try {
      const response = await DashboardController.getOrderTypeWiseStats(dateRange.start_date, dateRange.end_date);

      if (response && response.success === true) {
        setOrderTypeWiseStats(response.data || []);
      } else {
        setOrderTypeWiseStats([]);
      }
    } catch (error) {
      console.error('Error fetching order type wise statistics:', error);
      setOrderTypeWiseStats([]);
    } finally {
      setOrderTypeStatsLoading(false);
    }
  }, [dateRange]);

  // Effect to fetch data when date range changes
  useEffect(() => {
    // Skip API calls once the user is logged out (token cleared)
    if (!accessToken) return;
    // Call the fetchDashboardData function on component mount and when date range changes
    fetchDashboardData();
    fetchRecentTransactions();
    fetchMonthlyChartData();
    fetchTopSellingLowSellingProducts();
    fetchLowStockProducts();
    fetchOrderTypeWiseStats();
  }, [dateRange, fetchDashboardData, fetchOrderTypeWiseStats, accessToken]);

  // Effect to listen for store change events and refresh dashboard data
  useEffect(() => {
    const handleStoreChange = () => {
      // Skip API calls once the user is logged out (token cleared)
      if (!accessToken) return;
      // Refresh all dashboard data when store changes
      fetchDashboardData();
      fetchRecentTransactions();
      fetchMonthlyChartData();
      fetchTopSellingLowSellingProducts();
      fetchLowStockProducts();
      fetchOrderTypeWiseStats();
    };

    // Listen for custom store change event
    window.addEventListener('dashboardStoreChange', handleStoreChange);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('dashboardStoreChange', handleStoreChange);
    };
  }, [fetchDashboardData, fetchRecentTransactions, fetchMonthlyChartData, fetchTopSellingLowSellingProducts, fetchLowStockProducts, fetchOrderTypeWiseStats, accessToken]);

  useEffect(() => {
    // console.log("topSellingLowSellingProductsData", topSellingLowSellingProductsData);
  }, [topSellingLowSellingProductsData]);
  return (
    <div className="dashboard-container">
      <Container fluid>
        {/* Header with title and date picker */}
        <div className="d-flex flex-wrap mb-2 justify-content-between">
          <div className="mr-auto">
            <div className="d-flex align-items-center">
              <div>
                <span className="text-title">{t('Dashboard')}</span>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            {/* Month picker */}
            {/* <DatePicker
              selected={selectedDate}
              onChange={handleMonthChange}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              className="form-control bg-white"
              minDate={new Date(2023, 0, 1)}
              //maxDate={new Date(2030, 11, 31)}
              openToDate={new Date()}
            /> */}
          </div>
        </div>

        {/* Date Filter Buttons */}
        <div className="d-flex flex-wrap mb-3 align-items-center justify-content-between" style={{ gap: '8px' }}>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <span className="text-subhead">Quick Overview</span>
            <span className="text-muted small">{`${moment(dateRange.start_date).format('DD-MM-YYYY')} - ${moment(dateRange.end_date).format('DD-MM-YYYY')}`}</span>
          </div>
          
          <div className="d-flex align-items-center" style={{ gap: '6px' }}>
            {[
              { key: 'TD', label: 'TD', color: '#007bff' },
              { key: 'YD', label: 'YD', color: '#ffc107' },
              { key: 'TW', label: 'TW', color: '#17a2b8' },
              { key: 'LW', label: 'LW', color: '#e83e8c' },
              { key: 'TM', label: 'TM', color: '#28a745' },
              { key: 'LM', label: 'LM', color: '#fd7e14' },
              { key: 'L6M', label: 'L6M', color: '#dc3545' },
              { key: 'TQ', label: 'TQ', color: '#17a2b8' },
              { key: 'LQ', label: 'LQ', color: '#e83e8c' },
              { key: 'TY', label: 'TY', color: '#6f42c1' },
              { key: 'LY', label: 'LY', color: '#20c997' },
              { key: 'CFY', label: 'CFY', color: '#6610f2' },
              { key: 'LFY', label: 'LFY', color: '#fd7e14' }
            ].map(filter => (
              <OverlayTrigger
                key={filter.key}
                placement="top"
                overlay={<Tooltip>{filterTooltips[filter.key]}</Tooltip>}
              >
                <button
                  onClick={() => handleFilterClick(filter.key)}
                  style={{
                    padding: '6px 12px',
                    border: selectedFilter === filter.key ? `2px solid ${filter.color}` : '1px solid #dee2e6',
                    borderRadius: '20px',
                    backgroundColor: selectedFilter === filter.key ? filter.color : '#fff',
                    color: selectedFilter === filter.key ? '#fff' : filter.color,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFilter !== filter.key) {
                      e.target.style.backgroundColor = `${filter.color}15`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFilter !== filter.key) {
                      e.target.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  {filter.label}
                </button>
              </OverlayTrigger>
            ))}
          </div>
        </div>

        {/* Date Range Filter - Optimized and Right-Aligned */}
        <div className="d-flex justify-content-end mb-3">
          <div className="d-flex align-items-center" style={{ 
            border: '1px solid #dee2e6', 
            borderRadius: '4px', 
            padding: '4px 8px',
            backgroundColor: '#f8f9fa',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <span className="text-muted small me-2" style={{ fontWeight: '500' }}>Date Range</span>
            
            <Form.Control
              type="date"
              size="sm"
              value={dateRange.start_date}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setDateRange(prev => {
                  // If end date is earlier than new start date, update end date to start date
                  const newEndDate = prev.end_date < newStartDate ? newStartDate : prev.end_date;
                  return {
                    ...prev,
                    start_date: newStartDate,
                    end_date: newEndDate
                  };
                });
                setSelectedFilter(''); // Clear filter selection when custom date is selected
              }}
              style={{ 
                width: '110px', 
                border: 'none', 
                backgroundColor: 'transparent',
                fontSize: '12px',
                padding: '2px 4px'
              }}
            />
            
            <span className="text-muted mx-1" style={{ fontSize: '14px' }}>→</span>
            
            <Form.Control
              type="date"
              size="sm"
              value={dateRange.end_date}
              min={dateRange.start_date}
              onChange={(e) => {
                const newEndDate = e.target.value;
                // Only allow end date to be >= start date
                if (newEndDate >= dateRange.start_date) {
                  setDateRange(prev => ({
                    ...prev,
                    end_date: newEndDate
                  }));
                  setSelectedFilter(''); // Clear filter selection when custom date is selected
                }
              }}
              style={{ 
                width: '110px', 
                border: 'none', 
                backgroundColor: 'transparent',
                fontSize: '12px',
                padding: '2px 4px'
              }}
            />
          </div>
        </div>

        {/* Today's Metrics section */}
        <div className="d-flex flex-wrap mb-2">
          {/* <div className="mr-auto">
            <div className="d-flex mb-2">
              <div>
                <span className="text-subhead-bold">{t('Today')}</span>
              </div>
            </div>
          </div> */}

          <div className="col-md-12">
            <div className="row">
              {/* Today's Orders Count */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  {/* <div className="d-flex p-3 flex-wrap box-content"> */}
                  <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0">
                    <div className="w-100">
                      <MetricCardWithComparison
                        title={t('POS Order Count')}
                        value={todaysOrderCount.raw}
                        difference={todaysOrderCount.difference}
                        isLoading={isLoading}
                        iscurrencyAdd={false}
                      />
                    </div>
                  </div>
                  {/* </div> */}
                </div>
                <div className="col-12 rounded custom-border-light mt-2">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0">
                      <div className='stat_chart_container' style={{ width: '100%', minHeight: '200px' }}>
                        {isLoading ? (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          <DashboardChart
                            type="line"
                            title={t('Sale Count')}
                            data={salesCountChartData}
                            height="180px"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Today's Orders Value */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  {/* <div className="d-flex p-3 flex-wrap box-content"> */}
                  <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0">
                    <div className="w-100">
                      <MetricCardWithComparison
                        title={t('POS Order Value')}
                        value={todaysOrderValue.raw}
                        difference={todaysOrderValue.difference}
                        isLoading={isLoading}
                        currency={storeCurrency}
                      />
                    </div>
                  </div>
                  {/* </div> */}
                </div>
                <div className="col-12 rounded custom-border-light mt-2">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0">
                      <div className='stat_chart_container' style={{ width: '100%', minHeight: '200px' }}>
                        {isLoading ? (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          <DashboardChart
                            type="line"
                            title={t('Sale Value')}
                            data={salesValueChartData}
                            height="180px"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* This Month section */}
        <div className="d-flex flex-wrap mb-2">
          {/* <div className="mr-auto">
            <div className="d-flex mb-2">
              <div>
                <span className="text-subhead-bold">{t('This Month')}</span>
              </div>
            </div>
          </div> */}

          <div className="col-md-12">
            <div className="row">
              {/* Total Sales count metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Total Sales')}
                  value={orderCount.raw}
                  difference={orderCount.difference}
                  isLoading={isLoading}
                  iscurrencyAdd={false}
                />
              </div>

              {/* Total POS Sale Value metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Total POS Sale Value')}
                  value={orderValue.raw}
                  difference={orderValue.difference}
                  isLoading={isLoading}
                  currency={storeCurrency}
                />
              </div>

              {/* Revenue Value metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Total Revenue')}
                  value={revenueValue.raw}
                  difference={revenueValue.difference}
                  isLoading={isLoading}
                  currency={storeCurrency}
                />
              </div>

              {/* Total Invoices metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Total Invoices')}
                  value={invoicesCount.raw}
                  difference={invoicesCount.difference}
                  isLoading={isLoading}
                  iscurrencyAdd={false}
                />
              </div>

              {/* Expense metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Total Expense')}
                  value={expense.raw}
                  difference={expense.difference}
                  isLoading={isLoading}
                  currency={storeCurrency}
                />
              </div>

              {/* Net Profit Value metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Net Profit')}
                  value={netProfitValue.raw}
                  difference={netProfitValue.difference}
                  isLoading={isLoading}
                  currency={storeCurrency}
                />
              </div>

              {/* Customer Count metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Total Customers')}
                  value={customerCount.raw}
                  difference={customerCount.difference}
                  isLoading={isLoading}
                  iscurrencyAdd={false}
                />
              </div>

              {/* Total Purchase Orders metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Total Purchase Orders')}
                  value={purchaseOrderCount.raw}
                  difference={purchaseOrderCount.difference}
                  isLoading={isLoading}
                  iscurrencyAdd={false}
                />
              </div>

              {/* Expense metric */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <MetricCardWithComparison
                  title={t('Footfall')}
                  value={footfallValue}
                  isLoading={isLoading}
                  iscurrencyAdd={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Targets Section */}
        <div className="d-flex flex-wrap mb-2">
          <div className="mr-auto">
            <div className="d-flex mb-2">
              <div>
                <span className="text-subhead-bold">{t('Targets')}</span>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="row">
              {/* Income Target */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold mb-3">{t('Income Target')}</h6>
                      {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                          <Spinner animation="border" size="sm" />
                        </div>
                      ) : (
                        <>
                          <div className="progress mb-2" style={{ height: '10px' }}>
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{ width: target.income_width }}
                              aria-valuenow={revenueValue.raw}
                              aria-valuemin="0"
                              aria-valuemax={target.income || 100}
                            ></div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>{Utility.formatCurrencyValue(revenueValue.raw)}</span>
                            <span>{Utility.formatCurrencyValue(target.income)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expense Target */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold mb-3">{t('Expense Target')}</h6>
                      {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                          <Spinner animation="border" size="sm" />
                        </div>
                      ) : (
                        <>
                          <div className="progress mb-2" style={{ height: '10px' }}>
                            <div
                              className="progress-bar bg-danger"
                              role="progressbar"
                              style={{ width: target.expense_width }}
                              aria-valuenow={expense.raw}
                              aria-valuemin="0"
                              aria-valuemax={target.expense || 100}
                            ></div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>{Utility.formatCurrencyValue(expense.raw)}</span>
                            <span>{Utility.formatCurrencyValue(target.expense)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* POS Sales Target */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold mb-3">{t('POS Sales Target')}</h6>
                      {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                          <Spinner animation="border" size="sm" />
                        </div>
                      ) : (
                        <>
                          <div className="progress mb-2" style={{ height: '10px' }}>
                            <div
                              className="progress-bar bg-primary"
                              role="progressbar"
                              style={{ width: target.sales_width }}
                              aria-valuenow={orderValue.raw}
                              aria-valuemin="0"
                              aria-valuemax={target.sales || 100}
                            ></div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>{Utility.formatCurrencyValue(orderValue.raw)}</span>
                            <span>{Utility.formatCurrencyValue(target.sales)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Profit Target */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-3 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold mb-3">{t('Net Profit Target')}</h6>
                      {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                          <Spinner animation="border" size="sm" />
                        </div>
                      ) : (
                        <>
                          <div className="progress mb-2" style={{ height: '10px' }}>
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{ width: target.net_profit_width }}
                              aria-valuenow={netProfitValue.raw}
                              aria-valuemin="0"
                              aria-valuemax={target.net_profit || 100}
                            ></div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>{Utility.formatCurrencyValue(netProfitValue.raw)}</span>
                            <span>{Utility.formatCurrencyValue(target.net_profit)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Activity Charts Section */}
        <div className="d-flex flex-wrap mb-2">
          <div className="mr-auto">
            <div className="d-flex mb-2">
              <div>
                {/* <span className="text-subhead-bold">{t('Monthly Activity')}</span> */}
                <span className="text-subhead-bold">{t('Activity')}</span>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="row">
              {/* Monthly Sales Count Activity Chart */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0">
                      <div className='stat_chart_container' style={{ width: '100%', minHeight: '260px' }}>
                        {monthlyChartLoading ? (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          <DashboardChart
                            type="line"
                            title={t('POS Sales Count Activity')}
                            data={monthlySalesCountChartData}
                            height="220px"
                            options={{
                              scales: {
                                y: {
                                  beginAtZero: true
                                }
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Sales Value Activity Chart */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0">
                      <div className='stat_chart_container' style={{ width: '100%', minHeight: '260px' }}>
                        {monthlyChartLoading ? (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          <DashboardChart
                            type="line"
                            title={t('POS Sales Value Activity')}
                            data={monthlySalesValueChartData}
                            height="220px"
                            options={{
                              scales: {
                                y: {
                                  beginAtZero: true
                                }
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Analysis Section */}
        <div className="d-flex flex-wrap mb-2">
          <div className="mr-auto">
            <div className="d-flex mb-2">
              <div>
                <span className="text-subhead-bold">{t('Financial Analysis')}</span>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="row">
              {/* Income/Expense Pie Chart */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0">
                      <div className='stat_chart_container' style={{ width: '100%', minHeight: '260px' }}>
                        {isLoading ? (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : (
                          <DashboardChart
                            type="pie"
                            title={t('Income/Expense')}
                            data={incomeExpenseChartData}
                            height="220px"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold">{t('Recent Transactions')}</h6>

                      {transactionsLoading ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                          <Spinner animation="border" />
                        </div>
                      ) : !recentTransactions || recentTransactions.length === 0 ? (
                        <div className="text-center my-4">
                          <p>{t('No recent transactions found')}</p>
                        </div>
                      ) : (
                        <div className="table-responsive transactions-table-box">
                          <Table className="mb-0">
                            <thead>
                              <tr>
                                <th>{t('Transaction')}</th>
                                <th>{t('Date')}</th>
                                <th>{t('Payment Method')}</th>
                                <th>{t('Amount')}</th>
                                <th>{t('Type')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recentTransactions.slice(0, 5).map((transaction, index) => (
                                <tr key={index}>
                                  <td>{transaction.transaction_code || '-'}</td>
                                  <td>{transaction.transaction_date || '-'}</td>
                                  <td>
                                    {transaction.payment_method && transaction.payment_method.length > 12
                                      ? `${transaction.payment_method.substring(0, 12)}...`
                                      : transaction.payment_method || '-'}
                                  </td>
                                  <td>
                                    {Utility.formatCurrencyValue(transaction.amount)}
                                  </td>
                                  <td>
                                    <span className={`badge ${(transaction.transaction_type_data?.label || '').includes('Income') ? 'bg-success' :
                                      (transaction.transaction_type_data?.label || '').includes('Expense') ? 'bg-danger' : 'bg-secondary'
                                      }`}>
                                      {transaction.transaction_type_data?.label || '-'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top 5 Most Selling Items */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold">{t('Top 5 most selling item')}</h6>

                      <div className="table-responsive transactions-table-box" style={{ maxHeight: '300px', minHeight: '300px', overflowY: 'auto' }}>
                        <Table className="mb-0" style={{ boxShadow: 'none' }}>
                          <thead>
                            <tr>
                              <th>{t('Product Name')}</th>
                              <th className="text-right">{t('Order Count')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topSellingLowSellingProductsData?.top_5 && topSellingLowSellingProductsData.top_5.length > 0 ? (
                              topSellingLowSellingProductsData.top_5.map((product, index) => (
                                <tr key={index}>
                                  <td>{product.name || '-'}</td>
                                  <td className="text-right">{product.ordered_count || 0}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="2" className="text-center">{t('No data available')}</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Top 5 Low Selling Items */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold">{t('Top 5 low selling item')}</h6>

                      <div className="table-responsive transactions-table-box" style={{ maxHeight: '300px', minHeight: '300px', overflowY: 'auto' }}>
                        <Table className="mb-0" style={{ boxShadow: 'none' }}>
                          <thead>
                            <tr>
                              <th>{t('Product Name')}</th>
                              <th className="text-right">{t('Order Count')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topSellingLowSellingProductsData?.low_5 && topSellingLowSellingProductsData.low_5.length > 0 ? (
                              topSellingLowSellingProductsData.low_5.map((product, index) => (
                                <tr key={index}>
                                  <td>{product.name || '-'}</td>
                                  <td className="text-right">{product.ordered_count || 0}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="2" className="text-center">{t('No data available')}</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Items Never Sold */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold">{t('Item never sold')}</h6>

                      <div className="table-responsive transactions-table-box" style={{ maxHeight: '300px', minHeight: '300px', overflowY: 'auto' }}>
                        <Table className="mb-0" style={{ boxShadow: 'none' }}>
                          <thead>
                            <tr>
                              <th>{t('Product Name')}</th>
                              {/* <th className="text-right">{t('Order Count')}</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {topSellingLowSellingProductsData?.never_ordered && topSellingLowSellingProductsData.never_ordered.length > 0 ? (
                              topSellingLowSellingProductsData.never_ordered.map((product, index) => (
                                <tr key={index}>
                                  <td>{product.name || '-'}</td>
                                  {/* <td className="text-right">{product.ordered_count || 0}</td> */}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="1" className="text-center">{t('No data available')}</td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Low Stock Products */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold">{t('Low Stock Products')}</h6>

                      {lowStockLoading ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                          <Spinner animation="border" />
                        </div>
                      ) : (
                        <div className="table-responsive transactions-table-box" style={{ maxHeight: '300px', minHeight: '300px', overflowY: 'auto' }}>
                          <Table className="mb-0" style={{ boxShadow: 'none' }}>
                            <thead>
                              <tr>
                                <th>{t('Product Code')}</th>
                                <th>{t('Name')}</th>
                                <th>{t('Category')}</th>
                                <th className="text-right">{t('Quantity Left')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lowStockProducts && lowStockProducts.length > 0 ? (
                                lowStockProducts.map((product, index) => (
                                  <tr key={index}>
                                    <td>{product.product_code || '-'}</td>
                                    <td>{product.name || '-'}</td>
                                    <td>{product.category || '-'}</td>
                                    <td className="text-right">{product.quantity || '0.00'}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">{t('No low stock products')}</td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Type Wise Statistics report */}
              <div className="d-flex align-items-start flex-column p-1 mb-1 col-md-6 box">
                <div className="col-12 rounded custom-border-light">
                  <div className="d-flex p-3 flex-wrap box-content">
                    <div className="d-flex col-sm-12 col-md-12 col-lg-12 col-xl-12 p-0 flex-column">
                      <h6 className="text-bold">{t('Order Type Wise Statistics')}</h6>

                      {orderTypeStatsLoading ? (
                        <div className="d-flex justify-content-center align-items-center my-4">
                          <Spinner animation="border" />
                        </div>
                      ) : (
                        <div className="table-responsive transactions-table-box" style={{ maxHeight: '300px', minHeight: '300px', overflowY: 'auto' }}>
                          <Table className="mb-0" style={{ boxShadow: 'none' }}>
                            <thead>
                              <tr>
                                <th>{t('Order Type')}</th>
                                <th className="text-right">{t('Order Count')}</th>
                                <th className="text-right">{t('Gross Booking')}</th>
                                <th className="text-right">{t('Net Bill')}</th>
                                <th className="text-right">{t('Avg Order Value')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orderTypeWiseStats && orderTypeWiseStats.length > 0 ? (
                                orderTypeWiseStats.map((stat, index) => (
                                  <tr key={index}>
                                    <td>{stat.order_type || '-'}</td>
                                    <td className="text-right">{stat.order_count || 0}</td>
                                    <td className="text-right">{Utility.formatCurrencyValue(stat.gross_booking_amount)}</td>
                                    <td className="text-right">{Utility.formatCurrencyValue(stat.net_bill_amount)}</td>
                                    <td className="text-right">{Utility.formatCurrencyValue(stat.average_order_value)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5" className="text-center">{t('No order type statistics available')}</td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

const mapStateToProps = (state) => ({
  accessToken: state.LoginReducer?.token || '',
  currentStore: state.StoreReducer?.currentStore || {},
  userEntryType: state.LoginReducer?.userEntryType || {},
});

export default connect(mapStateToProps)(withNamespaces()(MainContent))
